import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import type { Product, SiteSettings } from "../shared/types";
import { formatAud } from "../shared/pricing";
import { useCart } from "./cart";

export function navigate(path: string) {
  if (window.location.pathname + window.location.search === path) return;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
}

export function Link({ href, className, children, onClick, ...rest }: {
  href: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
  [key: string]: unknown;
}) {
  function handle(event: MouseEvent<HTMLAnchorElement>) {
    if (!event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0 && href.startsWith("/")) {
      event.preventDefault();
      onClick?.();
      navigate(href);
    }
  }
  return <a href={href} className={className} onClick={handle} {...rest}>{children}</a>;
}

export function usePageMeta(title: string, description: string) {
  useEffect(() => {
    document.title = title;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, [title, description]);
}

export function Header({ settings }: { settings: SiteSettings }) {
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return <>
    {settings.announcement && <div className="announcement">{settings.announcement}</div>}
    <header className="site-header">
      <Link href="/" className="brand-link" aria-label="Pressed Studio Signs home">
        <img src="/media/brand/pressed-studio-logo-header.svg" alt="Pressed Studio Signs" />
      </Link>
      <button className="menu-button" type="button" aria-expanded={open} aria-controls="primary-navigation" onClick={() => setOpen(!open)}>
        <span></span><span></span><span></span><span className="sr-only">Menu</span>
      </button>
      <nav id="primary-navigation" className={open ? "nav is-open" : "nav"} aria-label="Primary navigation">
        <Link href="/shop" onClick={close}>Shop</Link>
        <Link href="/custom-signs" onClick={close}>Custom signs</Link>
        <Link href="/about" onClick={close}>About</Link>
        <Link href="/faq" onClick={close}>Questions</Link>
      </nav>
      <Link href="/cart" className="bag-link" aria-label={`Bag with ${count} items`}>
        Bag <span aria-hidden="true">({count})</span>
      </Link>
    </header>
  </>;
}

export function Footer({ settings }: { settings: SiteSettings }) {
  return <footer className="footer">
    <div className="footer__brand">
      <h2>Pressed Studio Signs</h2>
      <p>Personalised fabric signage, made for weddings, events and meaningful occasions.</p>
    </div>
    <div>
      <h3>Information</h3>
      <nav aria-label="Footer navigation">
        <Link href="/shop">Shop</Link>
        <Link href="/custom-signs">Custom signs</Link>
        <Link href="/about">About</Link>
        <Link href="/faq">Questions</Link>
        <Link href="/contact">Contact</Link>
      </nav>
    </div>
    <div>
      <h3>Policies</h3>
      <nav aria-label="Policy navigation">
        <Link href="/policies/shipping">Shipping</Link>
        <Link href="/policies/returns">Returns</Link>
        <Link href="/policies/privacy">Privacy</Link>
        <Link href="/policies/terms">Terms</Link>
      </nav>
    </div>
    <div>
      <h3>Follow</h3>
      <a href={settings.instagramUrl} target="_blank" rel="noreferrer">Instagram ↗</a>
    </div>
    <p className="footer__legal">© {new Date().getFullYear()} Pressed Studio Signs</p>
  </footer>;
}

export function Layout({ settings, children }: { settings: SiteSettings; children: ReactNode }) {
  return <div className="site-shell">
    <a className="skip-link" href="#main">Skip to content</a>
    <Header settings={settings} />
    <main id="main">{children}</main>
    <Footer settings={settings} />
  </div>;
}

export function ProductCard({ product, priority = false }: { product: Product; priority?: boolean }) {
  const image = product.media.find((item) => item.kind === "image");
  const availablePrices = product.variants.filter((item) => item.available).map((item) => item.priceCents);
  const from = availablePrices.length ? Math.min(...availablePrices) : null;
  return <article className="product-card">
    <Link href={`/product/${product.slug}`} className="product-card__image">
      {image && <img src={image.url} alt={image.alt} loading={priority ? "eager" : "lazy"} />}
    </Link>
    <div className="product-card__body">
      <p className="eyebrow">{product.material}</p>
      <h3><Link href={`/product/${product.slug}`}>{product.name}</Link></h3>
      <p>{from === null ? "Currently unavailable" : `From ${formatAud(from)} AUD`}</p>
    </div>
  </article>;
}

export function StatusMessage({ type = "info", children }: { type?: "info" | "error" | "success"; children: ReactNode }) {
  return <div className={`status status--${type}`} role={type === "error" ? "alert" : "status"}>{children}</div>;
}

export function Loading() {
  return <div className="loading" role="status"><span></span><span className="sr-only">Loading</span></div>;
}
